import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { notification } from "antd";
import { useNavigate } from "react-router-dom";
import {
  IoCalendarOutline,
  IoGameControllerOutline,
  IoPeopleOutline,
  IoPersonOutline,
  IoTimeOutline,
  IoTrophy,
  IoGiftOutline,
  IoMicOffOutline,
  IoMicOutline,
  IoVolumeHighOutline,
  IoVolumeMuteOutline,
} from "react-icons/io5";
import { resolveAvatarSrc } from "../../utils/avatarUrl";
import ChampionshipArchive from "../../components/championship/ChampionshipArchive";
import ChampionshipBracket from "../../components/championship/ChampionshipBracket";
import ChampionshipChat from "../../components/championship/ChampionshipChat";
import useChampionshipLive from "../../components/championship/useChampionshipLive";
import "./championship.css";
import {
  isGuestMustRegisterError,
  openGuestChampionshipRegister,
} from "../../components/championship/guestChampionshipPrompt";

const ROUND_TITLES = {
  final: "GRAN FINAL",
  semifinal: "SEMIFINALES",
  quarterfinal: "CUARTOS DE FINAL",
  roundOf16: "OCTAVOS DE FINAL",
};
const ROUND_SHORT = {
  final: "F",
  semifinal: "SF",
  quarterfinal: "CF",
  roundOf16: "OF",
};
const MODE_LABELS = {
  ciencia: "Ciencia",
  azar: "Azar",
  cienzar: "Cienzar",
  contrazar: "Contrazar",
};

const TIME_OPTIONS = [1, 2, 3, 5, 10, 15, 20, 30, 60];

const pad = (value) => String(value).padStart(2, "0");

const START_MODE_LABELS = { all: "Todos a la vez", staggered: "Escalonado", manual: "Manual" };
const START_MODE_HELP = {
  all: "Todos los partidos de cada ronda empiezan a la misma hora; la ronda siguiente empieza al acabar la anterior más el descanso.",
  staggered: "Un partido detrás de otro (1 contra 2, luego 3 contra 4…), y al acabar una ronda sigue la siguiente con el mismo ritmo.",
  manual: "Tú pones la hora de cada partido en el calendario. Sin hora, un partido empieza en cuanto entran los dos.",
};

const formatSchedule = (value) => {
  if (!value) return "Libre · sin hora";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Por definir";
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} - ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Value for <input type="datetime-local"> in the viewer's local time.
const toLocalInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
};

const formatCountdown = (target, now) => {
  if (!target) return "";
  const diff = new Date(target).getTime() - now;
  if (!Number.isFinite(diff)) return "";
  if (diff <= 0) return "En juego / por jugar";
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return days > 0
    ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const entryLabel = (entry, roundKeyById) => {
  if (!entry) return "—";
  if (entry.kind === "player") return entry.name;
  if (entry.kind === "bot") return "Bot XIMA";
  if (entry.kind === "open") return `Jugador ${entry.seat}`;
  if (entry.kind === "none") return "Nadie (no se presentó)";
  if (entry.kind === "winner") {
    const short = ROUND_SHORT[roundKeyById[entry.matchId]] || "R";
    return `Ganador ${short} ${entry.index}`;
  }
  return "—";
};

const PlayerAvatar = ({ entry, size = "md" }) => {
  const src = entry?.kind === "player" ? resolveAvatarSrc(entry.avatarUrl || "") : "";
  const initials =
    entry?.kind === "bot"
      ? "🤖"
      : entry?.kind === "player"
      ? String(entry.name || "?").trim().slice(0, 2).toUpperCase()
      : "?";
  return (
    <span className={`xc-avatar xc-avatar-${size}${entry?.kind === "open" ? " is-open" : ""}`}>
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
};

const Championship = () => {
  const navigate = useNavigate();
  const live = useChampionshipLive();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [calendarTab, setCalendarTab] = useState("upcoming");
  const [editingMatchId, setEditingMatchId] = useState("");
  const [draftSchedule, setDraftSchedule] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftSettings, setDraftSettings] = useState(null);
  // Contraseña que escribe el jugador al unirse (solo si el admin puso una).
  const [joinPasswordOpen, setJoinPasswordOpen] = useState(false);
  const [joinPassword, setJoinPassword] = useState("");
  const [now, setNow] = useState(() => Date.now());

  const showError = (error, fallback) => {
    if (isGuestMustRegisterError(error)) {
      openGuestChampionshipRegister(navigate);
      return;
    }
    notification.error({ message: error?.response?.data?.message || fallback });
  };

  const load = useCallback(async () => {
    try {
      const response = await axios.get("/championship");
      setData(response.data);
    } catch (error) {
      showError(error, "No se pudo cargar el campeonato.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = window.setInterval(load, 30000);
    return () => window.clearInterval(refresh);
  }, [load]);

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);

  const runAction = async (request, fallback) => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await request();
      setData(response.data);
      return true;
    } catch (error) {
      showError(error, fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const rounds = useMemo(() => data?.rounds || [], [data]);
  const roundKeyById = useMemo(() => {
    const map = {};
    rounds.forEach((round) => round.matches.forEach((match) => (map[match.id] = round.key)));
    return map;
  }, [rounds]);
  // Final on top, first round at the bottom.
  const roundsTopDown = useMemo(() => [...rounds].reverse(), [rounds]);
  const firstRound = rounds[0]?.matches || [];
  const seatEntries = firstRound.flatMap((match) => [match.a, match.b]);

  const players = data?.players || [];
  const me = String(data?.me || "");
  const isMyMatch = (match) =>
    !match.winner && [match.a, match.b].some((entry) => entry?.kind === "player" && entry.userId === me);
  const myOpenMatch = rounds.flatMap((round) => round.matches).find((match) => isMyMatch(match));

  const enterMyMatch = async (match) => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await axios.post(`/championship/matches/${match.id}/enter`);
      const tournamentId = String(response?.data?.tournamentId || "");
      const matchPassword = String(response?.data?.matchPassword || "");
      await axios.post("/tournaments/join", { id: tournamentId, matchPassword });
      // La contraseña viaja con la navegación: la partida no la vuelve a pedir.
      navigate(`/game/${tournamentId}?autoplay=1`, {
        state: { fromLobbySource: "championship", protectedMatchPassword: matchPassword },
      });
    } catch (error) {
      showError(error, "No se pudo entrar al partido.");
    } finally {
      setBusy(false);
    }
  };

  const openReplay = (historyId, match) => {
    navigate("/game/history", {
      state: {
        replayReturnTo: "/game/championship",
        openReplayRequestKey: `championship:${historyId}:${Date.now()}`,
        openReplayMatch: {
          id: historyId,
          historyId,
          hasReplay: true,
          tournamentName: data?.title || "Campeonato XIMA",
          participants: [match.a, match.b]
            .filter(Boolean)
            .map((entry) => ({ displayName: entry.name || (entry.kind === "bot" ? "Bot XIMA" : "") })),
        },
      },
    });
  };

  // Tocar un partido: en vivo se mira, terminado se reproduce.
  const handleMatchClick = (match) => {
    if (match.live && match.tournamentId && match.roomId) {
      navigate(
        `/game/${match.tournamentId}?autoplay=1&spectator=1&roomId=${encodeURIComponent(match.roomId)}`,
        { state: { fromLobbySource: "championship" } },
      );
      return;
    }
    const historyId = match.replayId || match.historyIds?.[match.historyIds.length - 1];
    if (historyId) openReplay(historyId, match);
  };
  const isAdmin = Boolean(data?.isAdmin);
  const isRegistration = data?.status === "registration";

  const startEditing = (match) => {
    if (!isAdmin) return;
    if (editingMatchId === match.id) {
      setEditingMatchId("");
      return;
    }
    setEditingMatchId(match.id);
    setDraftSchedule(toLocalInputValue(match.scheduledAt));
  };

  const saveMatch = async (match) => {
    const saved = await runAction(
      () =>
        axios.put(`/championship/admin/matches/${match.id}`, {
          scheduledAt: !match.clearSchedule && draftSchedule ? new Date(draftSchedule).toISOString() : null,
        }),
      "No se pudo guardar el partido.",
    );
    if (saved) setEditingMatchId("");
  };

  const openSettings = () => {
    setDraftSettings({
      title: data?.title || "Campeonato XIMA",
      prize: data?.prize || "",
      joinPassword: data?.joinPassword || "",
      startAt: data?.startAt ? toLocalInputValue(data.startAt) : "",
      startMode: data?.startMode || "manual",
      restMinutes: Number.isFinite(Number(data?.restMinutes)) ? Number(data.restMinutes) : 3,
      gameMode: data?.gameMode || "ciencia",
      timeMinutes: data?.timeMinutes || 5,
      maxPlayers: data?.maxPlayers || 12,
      status: data?.status || "registration",
    });
    setSettingsOpen((open) => !open);
  };

  const saveSettings = async () => {
    const saved = await runAction(
      () => axios.put("/championship/admin/settings", draftSettings),
      "No se pudo guardar el campeonato.",
    );
    if (saved) setSettingsOpen(false);
  };

  // Base de la pirámide: el cuadrado entero es la foto (o las iniciales); el
  // número y el nombre van debajo, fuera del cuadrado.
  const renderSeatCard = (entry) => (
    <>
      <div className={`xc-seat-card${entry?.kind === "bot" ? " is-bot" : ""}${entry?.kind === "open" ? " is-open" : ""}`}>
        <PlayerAvatar entry={entry} size="fill" />
      </div>
      <div className={`xc-seat-label${entry?.kind === "open" ? " is-open" : ""}`}>
        <b>{entry?.kind === "bot" ? "BOT" : entry?.seat}</b>
        <span>{entry?.kind === "open" ? "Libre" : entryLabel(entry, roundKeyById)}</span>
      </div>
    </>
  );

  const joinChampionship = async () => {
    if (data?.hasJoinPassword && !joinPasswordOpen) {
      setJoinPasswordOpen(true);
      return;
    }
    const joined = await runAction(
      () => axios.post("/championship/join", data?.hasJoinPassword ? { password: joinPassword } : {}),
      "No se pudo unir.",
    );
    if (joined) {
      setJoinPasswordOpen(false);
      setJoinPassword("");
    }
    live.refresh();
  };

  // Inicio del campeonato: hora de inicio, modo (a la vez, escalonado, manual) y descanso.
  const scheduleChampionship = async () => {
    const startDate = draftSettings.startAt ? new Date(draftSettings.startAt) : null;
    if (draftSettings.startMode !== "manual" && !startDate) {
      window.alert("Pon primero el día y la hora de inicio.");
      return;
    }
    const saved = await runAction(
      () =>
        axios.post("/championship/admin/schedule", {
          startAt: startDate ? startDate.toISOString() : null,
          startMode: draftSettings.startMode,
          restMinutes: Number(draftSettings.restMinutes) || 0,
        }),
      "No se pudo programar el campeonato.",
    );
    if (saved) setSettingsOpen(false);
  };

  const archiveChampionship = () => {
    if (!window.confirm("¿Terminar el campeonato y archivarlo? Se guardará tal como está y empezará uno nuevo vacío.")) return;
    runAction(() => axios.post("/championship/admin/archive"), "No se pudo archivar el campeonato.").then((saved) => {
      if (saved) setSettingsOpen(false);
    });
  };

  const resetChampionship = () => {
    if (!window.confirm("¿Reiniciar el campeonato? Se borran los inscritos, horarios y resultados.")) return;
    runAction(() => axios.post("/championship/admin/reset"), "No se pudo reiniciar el campeonato.");
  };

  // Each card is a button: the two players enter their game, the rest watch it
  // live, and once it is over it plays the replay.
  const matchAction = (match) => {
    const historyId = match.replayId || match.historyIds?.[match.historyIds.length - 1];
    if (match.winner) {
      return historyId ? { label: "▶ Reproducir juego", tone: "replay", run: () => openReplay(historyId, match) } : null;
    }
    if (isMyMatch(match) && match.tournamentId) {
      return { label: "▶ JUGAR", tone: "play", run: () => enterMyMatch(match) };
    }
    if (match.live && match.tournamentId && match.roomId) {
      return { label: "▶ Ver partido", tone: "watch", run: () => handleMatchClick(match) };
    }
    return null;
  };

  // Short names so they fit inside the fixed squares.
  const squareLabel = (entry) => {
    if (entry?.kind === "winner") return `Gan. ${ROUND_SHORT[roundKeyById[entry.matchId]] || "R"} ${entry.index}`;
    if (entry?.kind === "none") return "Nadie";
    return entryLabel(entry, roundKeyById);
  };

  // Tras la hora de inicio: al que falta se le avisa de cuánto le queda para entrar.
  const lateWarning = (match) => {
    if (match.winner || match.live || !match.scheduledAt) return "";
    const left = new Date(match.scheduledAt).getTime() + (Number(data?.timeMinutes) || 5) * 60000 - now;
    if (new Date(match.scheduledAt).getTime() > now || left <= 0) return "";
    const seconds = Math.ceil(left / 1000);
    const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
    return isMyMatch(match) ? `Si no te conectas en ${clock}, perderás la partida` : `Esperando jugadores · ${clock}`;
  };

  const renderMatchCard = (match, roundKey) => {
    const nameA = squareLabel(match.a);
    const nameB = squareLabel(match.b);
    const warning = lateWarning(match);
    const countdown =
      warning || (!match.winner && !match.live && new Date(match.scheduledAt || 0).getTime() > now
        ? formatCountdown(match.scheduledAt, now)
        : "");
    const action = matchAction(match);
    const Tag = action ? "button" : "div";
    return (
      <Tag
        key={match.id}
        type={action ? "button" : undefined}
        onClick={action ? action.run : undefined}
        disabled={action ? busy : undefined}
        className={`xc-match xc-round-${roundKey}${match.winner ? " is-done" : ""}${
          action ? ` is-clickable is-${action.tone}` : ""
        }`}
      >
        {match.live ? <b className="xc-live">● EN VIVO</b> : null}
        {match.winner === "a" || match.winner === "b" ? (
          // Terminado: «Ganador», su nombre en grande y el rival atenuado.
          <>
            <em className="xc-winner-label">Ganador</em>
            <span className="is-winner xc-winner-name">{match.winner === "a" ? nameA : nameB}</span>
          </>
        ) : (
          <>
            <span>{nameA}</span>
            {match.live ? null : <small>VS</small>}
            <span>{nameB}</span>
          </>
        )}
        {match.winner === "none" ? <em className="xc-none-line">No se presentó ninguno</em> : null}
        {match.winner || match.live || countdown ? null : <em>{formatSchedule(match.scheduledAt)}</em>}
        {countdown ? <b className="xc-countdown">{countdown}</b> : null}
        {action ? <b className={`xc-card-action is-${action.tone}`}>{action.label}</b> : null}
      </Tag>
    );
  };

  const calendarRounds = roundsTopDown.map((round) => ({
    ...round,
    matches: round.matches.filter((match) => calendarTab === "all" || !match.winner),
  }));

  if (loading && !data) {
    return <div className="xc-page xc-loading">Cargando campeonato…</div>;
  }

  return (
    <div className="xc-page">
      <div className="xc-layout">
        <aside className="xc-panel xc-calendar">
          <div className="xc-panel-title">
            <IoCalendarOutline aria-hidden="true" />
            <span>CALENDARIO</span>
          </div>
          <div className="xc-tabs">
            <button
              type="button"
              className={calendarTab === "upcoming" ? "is-active" : ""}
              onClick={() => setCalendarTab("upcoming")}
            >
              PRÓXIMOS
            </button>
            <button
              type="button"
              className={calendarTab === "all" ? "is-active" : ""}
              onClick={() => setCalendarTab("all")}
            >
              TODOS
            </button>
          </div>
          {data?.startAt ? (
            <p className="xc-start-info">
              Inicio: <b>{formatSchedule(data.startAt)}</b>
              {" · "}
              {START_MODE_LABELS[data.startMode] || "Manual"}
              {data.startMode !== "manual" ? ` · descanso ${Number(data.restMinutes) || 0} min` : ""}
            </p>
          ) : null}
          {isAdmin ? <p className="xc-hint">Toca un partido para cambiar su fecha y hora. El ganador sale solo del resultado de la partida.</p> : null}
          <div className="xc-calendar-list">
            {calendarRounds.map((round) =>
              round.matches.length ? (
                <div key={round.key} className={`xc-calendar-round xc-round-${round.key}`}>
                  <h3>{ROUND_TITLES[round.key] || round.key}</h3>
                  {round.matches.map((match) => {
                    const editing = editingMatchId === match.id;
                    const countdown = !match.winner && !match.live ? formatCountdown(match.scheduledAt, now) : "";
                    return (
                      <div key={match.id} className={`xc-calendar-item${editing ? " is-editing" : ""}`}>
                        <button
                          type="button"
                          className="xc-calendar-row"
                          onClick={() => startEditing(match)}
                          disabled={!isAdmin || Boolean(match.winner) || match.live}
                        >
                          <span>
                            {entryLabel(match.a, roundKeyById)} vs {entryLabel(match.b, roundKeyById)}
                          </span>
                          <small>
                            {formatSchedule(match.scheduledAt)}
                            {countdown ? <b> · {countdown}</b> : null}
                            {match.winner ? <b> · Ganó {entryLabel(match[match.winner], roundKeyById)}</b> : null}
                            {match.live ? <b> · EN VIVO</b> : null}
                            {match.note ? <i> · {match.note}</i> : null}
                          </small>
                          <IoTimeOutline aria-hidden="true" />
                        </button>
                        {isMyMatch(match) && match.tournamentId ? (
                          <button
                            type="button"
                            className="xc-play-mine is-small"
                            disabled={busy}
                            onClick={() => enterMyMatch(match)}
                          >
                            ▶ Jugar mi partido
                          </button>
                        ) : null}
                        {match.historyIds?.length ? (
                          <div className="xc-replays">
                            {match.historyIds.map((historyId, index) => (
                              <button key={historyId} type="button" onClick={() => openReplay(historyId, match)}>
                                ▶ {match.historyIds.length > 1 ? `Partida ${index + 1}` : "Repetición"}
                              </button>
                            ))}
                          </div>
                        ) : null}
                        {editing ? (
                          <div className="xc-calendar-editor">
                            {/* Fecha, hora y minuto por separado: se elige y se pulsa OK, sin
                                tener que hacer clic fuera del selector del navegador. */}
                            <div className="xc-schedule-picker">
                              <input
                                type="date"
                                aria-label="Día del partido"
                                value={draftSchedule.slice(0, 10)}
                                onChange={(event) =>
                                  setDraftSchedule(`${event.target.value}T${draftSchedule.slice(11, 16) || "20:00"}`)
                                }
                              />
                              <select
                                aria-label="Hora"
                                value={draftSchedule.slice(11, 13) || "20"}
                                onChange={(event) =>
                                  setDraftSchedule(
                                    `${draftSchedule.slice(0, 10) || toLocalInputValue(new Date()).slice(0, 10)}T${
                                      event.target.value
                                    }:${draftSchedule.slice(14, 16) || "00"}`,
                                  )
                                }
                              >
                                {Array.from({ length: 24 }, (_, hour) => (
                                  <option key={hour} value={pad(hour)}>
                                    {pad(hour)} h
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label="Minutos"
                                value={draftSchedule.slice(14, 16) || "00"}
                                onChange={(event) =>
                                  setDraftSchedule(
                                    `${draftSchedule.slice(0, 10) || toLocalInputValue(new Date()).slice(0, 10)}T${
                                      draftSchedule.slice(11, 13) || "20"
                                    }:${event.target.value}`,
                                  )
                                }
                              >
                                {Array.from({ length: 12 }, (_, step) => (
                                  <option key={step} value={pad(step * 5)}>
                                    {pad(step * 5)} min
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="xc-editor-actions">
                              <button
                                type="button"
                                className="xc-primary"
                                disabled={busy || draftSchedule.length < 16}
                                onClick={() => saveMatch(match)}
                              >
                                OK
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => {
                                  setDraftSchedule("");
                                  saveMatch({ ...match, clearSchedule: true });
                                }}
                              >
                                Quitar hora
                              </button>
                              <button type="button" onClick={() => setEditingMatchId("")}>
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null,
            )}
          </div>
          <p className="xc-legend">OF: Octavos · CF: Cuartos · SF: Semifinales</p>
        </aside>

        <main className="xc-center">
          <header className="xc-header">
            <IoTrophy className="xc-header-trophy" aria-hidden="true" />
            <div>
              <h1>{(data?.title || "Campeonato XIMA").toUpperCase()}</h1>
              <p>Demuestra tu habilidad y conviértete en el campeón</p>
            </div>
          </header>


          {isAdmin && settingsOpen && draftSettings ? (
            <div className="xc-settings-overlay" role="dialog" aria-modal="true">
            <section className="xc-panel xc-settings">
              <button type="button" className="xc-settings-close" onClick={() => setSettingsOpen(false)} aria-label="Cerrar">
                ×
              </button>
              <label>
                Nombre
                <input
                  value={draftSettings.title}
                  onChange={(event) => setDraftSettings({ ...draftSettings, title: event.target.value })}
                />
              </label>
              <label className="xc-settings-prize">
                Premio (texto libre)
                <input
                  value={draftSettings.prize}
                  maxLength={300}
                  placeholder="Ej.: 100 USD, 5.000 monedas, trofeo…"
                  onChange={(event) => setDraftSettings({ ...draftSettings, prize: event.target.value })}
                />
              </label>
              <label className="xc-settings-prize">
                Contraseña para unirse (vacía = sin contraseña)
                <input
                  value={draftSettings.joinPassword}
                  maxLength={40}
                  autoComplete="off"
                  placeholder="Sin contraseña"
                  onChange={(event) => setDraftSettings({ ...draftSettings, joinPassword: event.target.value })}
                />
              </label>
              <label>
                Modo de juego
                <select
                  value={draftSettings.gameMode}
                  onChange={(event) => setDraftSettings({ ...draftSettings, gameMode: event.target.value })}
                >
                  {Object.entries(MODE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Tiempo (min)
                <select
                  value={draftSettings.timeMinutes}
                  onChange={(event) => setDraftSettings({ ...draftSettings, timeMinutes: Number(event.target.value) })}
                >
                  {TIME_OPTIONS.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      {minutes} min
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Máx. jugadores
                <input
                  type="number"
                  min="2"
                  max="16"
                  value={draftSettings.maxPlayers}
                  onChange={(event) => setDraftSettings({ ...draftSettings, maxPlayers: event.target.value })}
                />
              </label>
              <label>
                Estado
                <select
                  value={draftSettings.status}
                  onChange={(event) => setDraftSettings({ ...draftSettings, status: event.target.value })}
                >
                  <option value="registration">Inscripción abierta</option>
                  <option value="running">En juego (cierra inscripción)</option>
                  <option value="finished">Terminado</option>
                </select>
              </label>
              <div className="xc-settings-actions">
                <button type="button" className="xc-primary" disabled={busy} onClick={saveSettings}>
                  Guardar
                </button>
              </div>

              <div className="xc-start-section">
                <h3>Inicio del campeonato</h3>
                <p>
                  A la hora de inicio se cierra la inscripción y los puestos libres se rellenan con bots.
                  Cada partido dura lo que suman los relojes de los dos jugadores
                  ({2 * (Number(draftSettings.timeMinutes) || 5)} min) más el descanso.
                </p>
                <div className="xc-schedule-picker">
                  <input
                    type="date"
                    aria-label="Día de inicio"
                    value={draftSettings.startAt.slice(0, 10)}
                    onChange={(event) =>
                      setDraftSettings({
                        ...draftSettings,
                        startAt: event.target.value
                          ? `${event.target.value}T${draftSettings.startAt.slice(11, 16) || "20:00"}`
                          : "",
                      })
                    }
                  />
                  <select
                    aria-label="Hora de inicio"
                    value={draftSettings.startAt.slice(11, 13) || "20"}
                    onChange={(event) =>
                      setDraftSettings({
                        ...draftSettings,
                        startAt: `${draftSettings.startAt.slice(0, 10) || toLocalInputValue(new Date()).slice(0, 10)}T${
                          event.target.value
                        }:${draftSettings.startAt.slice(14, 16) || "00"}`,
                      })
                    }
                  >
                    {Array.from({ length: 24 }, (_, hour) => (
                      <option key={hour} value={pad(hour)}>
                        {pad(hour)} h
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Minutos de inicio"
                    value={draftSettings.startAt.slice(14, 16) || "00"}
                    onChange={(event) =>
                      setDraftSettings({
                        ...draftSettings,
                        startAt: `${draftSettings.startAt.slice(0, 10) || toLocalInputValue(new Date()).slice(0, 10)}T${
                          draftSettings.startAt.slice(11, 13) || "20"
                        }:${event.target.value}`,
                      })
                    }
                  >
                    {Array.from({ length: 12 }, (_, step) => (
                      <option key={step} value={pad(step * 5)}>
                        {pad(step * 5)} min
                      </option>
                    ))}
                  </select>
                </div>
                <div className="xc-start-modes" role="group" aria-label="Cómo empiezan los partidos">
                  {Object.entries(START_MODE_LABELS).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      className={draftSettings.startMode === mode ? "is-active" : ""}
                      onClick={() => setDraftSettings({ ...draftSettings, startMode: mode })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="xc-start-mode-help">{START_MODE_HELP[draftSettings.startMode]}</p>
                {draftSettings.startMode !== "manual" ? (
                  <label>
                    Descanso entre partidos
                    <select
                      value={draftSettings.restMinutes}
                      onChange={(event) =>
                        setDraftSettings({ ...draftSettings, restMinutes: Number(event.target.value) })
                      }
                    >
                      {[0, 1, 2, 3, 5, 10, 15, 20, 30].map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {minutes === 0 ? "Sin descanso" : `${minutes} min`}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <button type="button" className="xc-primary" disabled={busy} onClick={scheduleChampionship}>
                  {draftSettings.startMode === "manual" ? "Guardar hora de inicio" : "Programar campeonato"}
                </button>
              </div>

              <div className="xc-settings-actions">
                <button type="button" className="xc-primary" disabled={busy} onClick={archiveChampionship}>
                  Terminar y archivar
                </button>
                <button type="button" className="xc-danger" disabled={busy} onClick={resetChampionship}>
                  Reiniciar campeonato
                </button>
              </div>
            </section>
            </div>
          ) : null}


          {myOpenMatch ? (
            <div className="xc-mine">
              <span>
                Tu partido: <b>{entryLabel(myOpenMatch.a, roundKeyById)}</b> vs{" "}
                <b>{entryLabel(myOpenMatch.b, roundKeyById)}</b> · {formatSchedule(myOpenMatch.scheduledAt)}
                {lateWarning(myOpenMatch)
                  ? ` · ${lateWarning(myOpenMatch)}`
                  : myOpenMatch.scheduledAt && !myOpenMatch.live
                  ? ` · empieza en ${formatCountdown(myOpenMatch.scheduledAt, now)}`
                  : ""}
              </span>
              {myOpenMatch.tournamentId ? (
                <button type="button" className="xc-play-mine" disabled={busy} onClick={() => enterMyMatch(myOpenMatch)}>
                  ▶ JUGAR
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="xc-stats">
            <div className="xc-stat-prize">
              <IoGiftOutline aria-hidden="true" />
              <span>
                <small>PREMIO</small>
                <strong title={data?.prize || "Por anunciar"}>{data?.prize || "Por anunciar"}</strong>
              </span>
            </div>
            <div>
              <IoGameControllerOutline aria-hidden="true" />
              <span>
                <small>MODO DE JUEGO</small>
                <strong>{MODE_LABELS[data?.gameMode] || "Ciencia"} · 1 vs 1</strong>
              </span>
            </div>
            <div>
              <IoPeopleOutline aria-hidden="true" />
              <span>
                <small>JUGADORES</small>
                <strong>
                  {players.length} / {data?.maxPlayers || 12}
                </strong>
              </span>
            </div>
            <div>
              <IoTimeOutline aria-hidden="true" />
              <span>
                <small>TIEMPO DE JUEGO</small>
                <strong>{data?.timeMinutes || 5} MIN</strong>
              </span>
            </div>
          </div>

          <ChampionshipBracket
            cornerSlot={
              isAdmin ? (
                <button type="button" className="xc-admin-button" onClick={openSettings}>
                  Ajustes del campeonato
                </button>
              ) : null
            }
            rounds={rounds}
            seats={seatEntries}
            roundTitles={ROUND_TITLES}
            renderMatch={renderMatchCard}
            renderSeat={renderSeatCard}
          />
        </main>

        <aside className="xc-panel xc-players">
          <div className="xc-panel-title">
            <IoPersonOutline aria-hidden="true" />
            <span>
              JUGADORES ({players.length}/{data?.maxPlayers || 12})
            </span>
          </div>
          <div className="xc-voice-bar">
            <button
              type="button"
              className={live.listening ? "is-on" : ""}
              onClick={live.toggleListening}
              title={live.listening ? "Dejar de escuchar a todos" : "Escuchar a todos"}
            >
              {live.listening ? <IoVolumeHighOutline /> : <IoVolumeMuteOutline />}
              <span>{live.listening ? "Escuchando" : "Escuchar"}</span>
            </button>
            {live.canSpeak || live.adminMutedMe ? (
              <button
                type="button"
                className={live.micActive ? "is-on" : live.adminMutedMe ? "is-blocked" : ""}
                onClick={live.toggleMic}
                title={live.adminMutedMe ? "El administrador silenció tu micrófono" : "Mi micrófono"}
              >
                {live.micActive ? <IoMicOutline /> : <IoMicOffOutline />}
                <span>{live.micActive ? "Hablando" : live.adminMutedMe ? "Silenciado" : "Hablar"}</span>
              </button>
            ) : null}
            {live.status ? <small>{live.status}</small> : null}
          </div>
          <div className="xc-players-list">
            {
              players.map((player) => {
                const voiceState = live.voice[player.userId] || {};
                const isMe = player.userId === live.me;
                const listenOn = live.listening && !live.listenMuted[player.userId];
                const micOn = isMe ? live.micActive : voiceState.micActive === true;
                return (
                  <div
                    key={player.userId}
                    className={`xc-player-row${live.speaking[player.userId] ? " is-speaking" : ""}`}
                  >
                    <span className="xc-player-seat">{player.seat}</span>
                    <PlayerAvatar entry={player} size="sm" />
                    <span className="xc-player-name">
                      {player.name}
                      <small>{player.points.toLocaleString("es-ES")} pts</small>
                    </span>
                    <button
                      type="button"
                      className={`xc-voice-btn${listenOn ? " is-on" : ""}`}
                      onClick={() => live.toggleListenTo(player.userId)}
                      disabled={isMe}
                      aria-label={listenOn ? `Dejar de escuchar a ${player.name}` : `Escuchar a ${player.name}`}
                      title={isMe ? "Eres tú" : listenOn ? "Escuchando" : "Escuchar"}
                    >
                      {listenOn ? <IoVolumeHighOutline /> : <IoVolumeMuteOutline />}
                    </button>
                    <button
                      type="button"
                      className={`xc-voice-btn${micOn ? " is-on" : ""}${voiceState.adminMuted ? " is-blocked" : ""}`}
                      onClick={() => {
                        if (isMe) live.toggleMic();
                        else if (live.isAdmin) live.setAdminMute(player.userId, !voiceState.adminMuted);
                      }}
                      disabled={!isMe && !live.isAdmin}
                      aria-label={
                        isMe
                          ? "Mi micrófono"
                          : live.isAdmin
                          ? voiceState.adminMuted
                            ? `Permitir hablar a ${player.name}`
                            : `Silenciar a ${player.name}`
                          : `Micrófono de ${player.name}`
                      }
                      title={
                        voiceState.adminMuted
                          ? live.isAdmin && !isMe
                            ? "Silenciado por ti: toca para permitirle hablar"
                            : "Silenciado por el administrador"
                          : isMe
                          ? "Hablar"
                          : live.isAdmin
                          ? "Toca para silenciarlo"
                          : micOn
                          ? "Micrófono abierto"
                          : "Micrófono cerrado"
                      }
                    >
                      {micOn ? <IoMicOutline /> : <IoMicOffOutline />}
                    </button>
                  </div>
                );
              })
            }
            {/* Todas las plazas del torneo: las libres apagadas, así el panel no cambia de tamaño. */}
            {Array.from(
              { length: Math.max(0, (Number(data?.maxPlayers) || 0) - players.length) },
              (_, index) => (
                <div key={`open-seat-${index}`} className="xc-player-row is-open-seat">
                  <span className="xc-player-seat">{players.length + index + 1}</span>
                  <span className="xc-avatar xc-avatar-sm is-open">?</span>
                  <span className="xc-player-name">Esperando jugador…</span>
                  <span />
                  <span />
                </div>
              ),
            )}
          </div>
          {isRegistration ? (
            data?.joined ? (
              <button
                type="button"
                className="xc-join is-leave"
                disabled={busy}
                onClick={() =>
                  runAction(() => axios.post("/championship/leave"), "No se pudo salir.").then(live.refresh)
                }
              >
                Salir del campeonato
              </button>
            ) : (
              <>
                {joinPasswordOpen ? (
                  <form
                    className="xc-join-password"
                    onSubmit={(event) => {
                      event.preventDefault();
                      joinChampionship();
                    }}
                  >
                    <label>
                      Este campeonato pide contraseña
                      <input
                        type="password"
                        value={joinPassword}
                        maxLength={40}
                        autoFocus
                        autoComplete="off"
                        placeholder="Escribe la contraseña"
                        onChange={(event) => setJoinPassword(event.target.value)}
                      />
                    </label>
                    <button type="button" onClick={() => setJoinPasswordOpen(false)}>
                      Cancelar
                    </button>
                  </form>
                ) : null}
                <button
                  type="button"
                  className="xc-join"
                  disabled={
                    busy ||
                    players.length >= (data?.maxPlayers || 12) ||
                    (joinPasswordOpen && !joinPassword.trim())
                  }
                  onClick={joinChampionship}
                >
                  {players.length >= (data?.maxPlayers || 12)
                    ? "Campeonato lleno"
                    : data?.hasJoinPassword
                    ? "🔒 UNIRME"
                    : "UNIRME"}
                </button>
              </>
            )
          ) : (
            <p className="xc-empty">La inscripción está cerrada.</p>
          )}
          <ChampionshipChat messages={live.chat} canChat={live.canChat} onSend={live.sendChat} />
        </aside>
      </div>
      <ChampionshipArchive
        refreshKey={String(data?.updatedAt || "")}
        onOpenReplay={openReplay}
        renderSeat={renderSeatCard}
      />
    </div>
  );
};

export default Championship;
